import {
  getLogger,
  LogCategory,
  LogEvent,
  LogLevel,
} from '@cntxt/shared/infrastructure';
import {
  isCancellationError,
  reportError,
} from '@cntxt/shared/utilities/errors';

/**
 * When `signIn()` runs, the browser soon navigates to the IdP and tears down the
 * in-flight NextAuth fetch. Engines surface that as AbortError, cancellation
 * messages, or TypeErrors with browser-specific text — not as a real failure.
 *
 * @param error - Rejection from `signIn(...).catch(...)`.
 * @returns `true` when the error should be ignored (expected abort).
 */
export function isExpectedSignInFetchAbort(error: unknown): boolean {
  if (isCancellationError(error)) {
    return true;
  }
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('failed to fetch') ||
      msg.includes('networkerror when attempting to fetch resource')
    );
  }
  return false;
}

/**
 * Handles `signIn` promise rejections: ignores expected IdP-navigation aborts;
 * logs and reports everything else. Safe to pass to `.catch()` (do not rethrow).
 *
 * @param logScope - Logger scope (e.g. component name) for diagnostics.
 * @param error - Rejection from `signIn(...)`.
 */
export function reportUnexpectedSignInRejection(
  logScope: string,
  error: unknown,
): void {
  if (isExpectedSignInFetchAbort(error)) {
    return;
  }

  const logger = getLogger(logScope);
  const message = 'Unexpected error during sign-in redirect';

  logger.contextualLog(LogLevel.ERROR, message, undefined, {
    category: LogCategory.AUTH,
    event: LogEvent.AUTH_FAILED,
    error: error instanceof Error ? error : new Error(String(error)),
  });
  reportError(error, message);
}
